'use client';

import { useState } from 'react';
import NextLink from 'next/link';
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building2,
  ChevronRight,
  ChevronLeft,
  Check,
  MapPin,
  Users,
  Wrench,
  AlertTriangle,
  BarChart3,
  Phone,
} from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { cn } from '@/lib/utils';

/* ═══════════════════════════════════════
   Step definitions
   ═══════════════════════════════════════ */

const STEPS = [
  { id: 1, title: 'Your Info', description: 'Create your account' },
  { id: 2, title: 'Your Properties', description: 'Tell us about your portfolio' },
  { id: 3, title: 'Your Operations', description: 'Current state of operations' },
  { id: 4, title: 'Your Goals', description: 'What you need help with' },
];

/* ═══════════════════════════════════════
   Option data
   ═══════════════════════════════════════ */

const propertyTypes = [
  { value: 'residential_rental', label: 'Residential Rental', sub: 'Apartments, multifamily, SFR' },
  { value: 'commercial_office', label: 'Commercial / Office', sub: 'Office buildings, co-working' },
  { value: 'retail', label: 'Retail', sub: 'Shopping centers, storefronts' },
  { value: 'industrial', label: 'Industrial / Warehouse', sub: 'Warehouses, distribution' },
  { value: 'mixed_use', label: 'Mixed-Use', sub: 'Residential + commercial' },
  { value: 'government', label: 'Government', sub: 'Federal, state, municipal' },
  { value: 'hospitality', label: 'Hospitality', sub: 'Hotels, vacation rentals' },
  { value: 'healthcare', label: 'Healthcare', sub: 'Clinics, medical offices' },
  { value: 'education', label: 'Education', sub: 'Schools, universities' },
  { value: 'hoa', label: 'HOA / Community', sub: 'Homeowner associations' },
  { value: 'other', label: 'Other', sub: 'Something else' },
];

const portfolioSizes = [
  { value: '1-5', label: '1-5 properties' },
  { value: '6-25', label: '6-25 properties' },
  { value: '26-100', label: '26-100 properties' },
  { value: '101-500', label: '101-500 properties' },
  { value: '500+', label: '500+ properties' },
];

const teamSizes = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-15', label: '6-15 people' },
  { value: '16-50', label: '16-50 people' },
  { value: '50+', label: '50+ people' },
];

const currentSoftware = [
  { value: 'spreadsheets', label: 'Spreadsheets / Manual' },
  { value: 'appfolio', label: 'AppFolio' },
  { value: 'buildium', label: 'Buildium' },
  { value: 'yardi', label: 'Yardi' },
  { value: 'maintainx', label: 'MaintainX' },
  { value: 'fmx', label: 'FMX' },
  { value: 'corrigo', label: 'Corrigo' },
  { value: 'maximo', label: 'IBM Maximo' },
  { value: 'other_cmms', label: 'Other CMMS' },
  { value: 'none', label: 'Nothing — pen & paper' },
];

const painPoints = [
  { value: 'work_orders', label: 'Work orders are chaotic', icon: Wrench },
  { value: 'vendor_management', label: 'Vendor coordination is a mess', icon: Users },
  { value: 'no_visibility', label: 'No visibility into operations', icon: BarChart3 },
  { value: 'maintenance_reactive', label: 'Maintenance is always reactive', icon: AlertTriangle },
  { value: 'compliance', label: 'Compliance / safety gaps', icon: AlertTriangle },
  { value: 'tenant_complaints', label: 'Tenant complaints piling up', icon: Phone },
  { value: 'cost_control', label: 'Can\'t control maintenance costs', icon: BarChart3 },
  { value: 'scaling', label: 'Operations don\'t scale', icon: Building2 },
];

const goals = [
  { value: 'streamline_ops', label: 'Streamline day-to-day operations' },
  { value: 'reduce_costs', label: 'Reduce maintenance costs' },
  { value: 'better_vendors', label: 'Build a better vendor network' },
  { value: 'preventive_maintenance', label: 'Move from reactive to preventive' },
  { value: 'compliance', label: 'Get compliant (OSHA, fire, ADA)' },
  { value: 'technology', label: 'Implement better technology' },
  { value: 'team_training', label: 'Train my team on best practices' },
  { value: 'grow_portfolio', label: 'Prepare to grow my portfolio' },
  { value: 'reduce_turnover', label: 'Reduce tenant turnover' },
  { value: 'data_reporting', label: 'Better data and reporting' },
];

const expectedOutcomes = [
  { value: 'save_time', label: 'Save time on daily operations' },
  { value: 'save_money', label: 'Reduce overall maintenance spend' },
  { value: 'fewer_emergencies', label: 'Fewer emergency breakdowns' },
  { value: 'happier_tenants', label: 'Happier tenants / fewer complaints' },
  { value: 'better_vendors', label: 'More reliable vendor relationships' },
  { value: 'peace_of_mind', label: 'Peace of mind — know nothing is falling through the cracks' },
  { value: 'scale_ready', label: 'Ready to scale without adding headcount' },
  { value: 'audit_ready', label: 'Audit-ready compliance documentation' },
  { value: 'data_driven', label: 'Make decisions backed by real data, not gut feel' },
  { value: 'professional_image', label: 'Look more professional to owners / investors' },
];

const urgency = [
  { value: 'asap', label: 'ASAP — things are on fire', color: 'text-danger-500 dark:text-danger-400' },
  { value: '1-3_months', label: '1-3 months', color: 'text-warning-600 dark:text-warning-400' },
  { value: '3-6_months', label: '3-6 months', color: 'text-primary-600 dark:text-primary-400' },
  { value: 'exploring', label: 'Just exploring options', color: 'text-secondary-500 dark:text-secondary-400' },
];

/* ═══════════════════════════════════════
   Multi-select chip component
   ═══════════════════════════════════════ */

function ChipSelect({
  options,
  selected,
  onToggle,
  columns = 2,
}: {
  options: { value: string; label: string; sub?: string; icon?: React.ComponentType<{ className?: string }> }[];
  selected: string[];
  onToggle: (value: string) => void;
  columns?: number;
}) {
  return (
    <div className={cn('grid gap-2', columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
      {options.map((opt) => {
        const isActive = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={cn(
              'flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition-all text-sm',
              isActive
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10 text-gray-900 dark:text-white'
                : 'border-gray-200 dark:border-secondary-700 text-gray-600 dark:text-secondary-400 hover:border-gray-300 dark:hover:border-secondary-600'
            )}
          >
            {opt.icon && <opt.icon className={cn('w-4 h-4 shrink-0', isActive ? 'text-primary-500' : 'text-gray-400 dark:text-secondary-500')} />}
            {isActive && !opt.icon && <Check className="w-4 h-4 shrink-0 text-primary-500" />}
            {!isActive && !opt.icon && <div className="w-4 h-4 shrink-0 rounded border border-gray-300 dark:border-secondary-600" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium">{opt.label}</div>
              {opt.sub && <div className="text-xs text-gray-400 dark:text-secondary-500">{opt.sub}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════
   Single-select component
   ═══════════════════════════════════════ */

function RadioSelect({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string; color?: string }[];
  selected: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      {options.map((opt) => {
        const isActive = selected === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition-all text-sm',
              isActive
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                : 'border-gray-200 dark:border-secondary-700 hover:border-gray-300 dark:hover:border-secondary-600'
            )}
          >
            <div className={cn(
              'w-4 h-4 shrink-0 rounded-full border-2 transition-all',
              isActive ? 'border-primary-500 bg-primary-500' : 'border-gray-300 dark:border-secondary-600'
            )}>
              {isActive && <div className="w-full h-full flex items-center justify-center"><div className="w-1.5 h-1.5 rounded-full bg-white" /></div>}
            </div>
            <span className={cn('font-medium', opt.color || 'text-gray-700 dark:text-secondary-300')}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════
   Register Page
   ═══════════════════════════════════════ */

const RegisterPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1: Account info
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2: Properties
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>([]);
  const [portfolioSize, setPortfolioSize] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [location, setLocation] = useState('');

  // Step 3: Operations
  const [software, setSoftware] = useState<string[]>([]);
  const [selectedPainPoints, setSelectedPainPoints] = useState<string[]>([]);
  const [operationsDescription, setOperationsDescription] = useState('');

  // Step 4: Goals & Outcomes
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const [successDefinition, setSuccessDefinition] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const [budget, setBudget] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggleMulti = (arr: string[], setArr: (v: string[]) => void, value: string) => {
    setArr(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!fullName.trim()) newErrors.fullName = 'Name is required';
      if (!email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Invalid email';
      if (!password) newErrors.password = 'Password is required';
      else if (password.length < 8) newErrors.password = 'At least 8 characters';
      if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords don\'t match';
    }
    if (step === 2) {
      if (selectedPropertyTypes.length === 0) newErrors.propertyTypes = 'Select at least one';
      if (!portfolioSize) newErrors.portfolioSize = 'Select portfolio size';
    }
    if (step === 3) {
      if (selectedPainPoints.length === 0) newErrors.painPoints = 'Select at least one';
    }
    if (step === 4) {
      if (selectedGoals.length === 0) newErrors.goals = 'Select at least one';
      if (!selectedUrgency) newErrors.urgency = 'Select timeline';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(STEPS.length, prev + 1));
    }
  };

  const prevStep = () => setCurrentStep(prev => Math.max(1, prev - 1));

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      // TODO: Wire to Supabase when ready
      setSubmitted(true);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="space-y-6 text-center py-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/20">
          <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            You&apos;re on the list
          </h1>
          <p className="text-gray-500 dark:text-secondary-400">
            We&apos;ve received your information and will reach out within 24 hours to schedule a discovery call.
          </p>
        </div>
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-left space-y-2">
          <p className="text-sm font-medium text-gray-900 dark:text-white">What happens next:</p>
          <ol className="text-sm text-gray-500 dark:text-secondary-400 space-y-1.5 list-decimal list-inside">
            <li>We review your intake to understand your operations</li>
            <li>We reach out to schedule a 30-minute discovery call</li>
            <li>We put together a custom recommendation based on your needs</li>
            <li>You get early access to Axis Datum when it launches</li>
          </ol>
        </div>
        <NextLink
          href="/login"
          className="inline-flex items-center justify-center w-full px-5 py-3 rounded-lg text-sm font-semibold bg-primary-500 text-white hover:bg-primary-400 transition-colors"
        >
          Go to Sign In
        </NextLink>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-1">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                currentStep > step.id
                  ? 'bg-green-500 text-white'
                  : currentStep === step.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-400 dark:bg-secondary-800 dark:text-secondary-500'
              )}
            >
              {currentStep > step.id ? <Check className="h-3.5 w-3.5" /> : step.id}
            </div>
            {index < STEPS.length - 1 && (
              <div className={cn(
                'h-0.5 w-6 mx-1 transition-colors',
                currentStep > step.id ? 'bg-green-500' : 'bg-gray-200 dark:bg-secondary-700'
              )} />
            )}
          </div>
        ))}
      </div>

      {/* Step header */}
      <div className="text-center space-y-1">
        <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
          {STEPS[currentStep - 1].title}
        </h1>
        <p className="text-sm text-gray-500 dark:text-secondary-400">
          {STEPS[currentStep - 1].description}
        </p>
        <p className="text-xs text-gray-400 dark:text-secondary-600">Step {currentStep} of {STEPS.length}</p>
      </div>

      {/* ═══ Step 1: Account Info ═══ */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <Input
            label="Full name"
            placeholder="Your name"
            leftIcon={<User className="h-4 w-4" />}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            error={errors.fullName}
          />
          <Input
            label="Email address"
            type="email"
            placeholder="you@company.com"
            leftIcon={<Mail className="h-4 w-4" />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <Input
            label="Phone (optional)"
            type="tel"
            placeholder="(555) 123-4567"
            leftIcon={<Phone className="h-4 w-4" />}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="At least 8 characters"
            leftIcon={<Lock className="h-4 w-4" />}
            rightIcon={
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
          />
          <Input
            label="Confirm password"
            type="password"
            placeholder="Confirm your password"
            leftIcon={<Lock className="h-4 w-4" />}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={errors.confirmPassword}
          />
        </div>
      )}

      {/* ═══ Step 2: Properties ═══ */}
      {currentStep === 2 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              What types of properties do you manage? <span className="text-xs text-gray-400">(select all)</span>
            </label>
            <ChipSelect
              options={propertyTypes}
              selected={selectedPropertyTypes}
              onToggle={(v) => toggleMulti(selectedPropertyTypes, setSelectedPropertyTypes, v)}
            />
            {errors.propertyTypes && <p className="text-sm text-danger-500 mt-1">{errors.propertyTypes}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              How many properties in your portfolio?
            </label>
            <RadioSelect options={portfolioSizes} selected={portfolioSize} onChange={setPortfolioSize} />
            {errors.portfolioSize && <p className="text-sm text-danger-500 mt-1">{errors.portfolioSize}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              Team size
            </label>
            <RadioSelect options={teamSizes} selected={teamSize} onChange={setTeamSize} />
          </div>

          <Input
            label="Primary location / market"
            placeholder="e.g. Dallas-Fort Worth, TX"
            leftIcon={<MapPin className="h-4 w-4" />}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
      )}

      {/* ═══ Step 3: Current Operations ═══ */}
      {currentStep === 3 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              What software do you currently use? <span className="text-xs text-gray-400">(select all)</span>
            </label>
            <ChipSelect
              options={currentSoftware}
              selected={software}
              onToggle={(v) => toggleMulti(software, setSoftware, v)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              What are your biggest pain points? <span className="text-xs text-gray-400">(select all)</span>
            </label>
            <ChipSelect
              options={painPoints}
              selected={selectedPainPoints}
              onToggle={(v) => toggleMulti(selectedPainPoints, setSelectedPainPoints, v)}
            />
            {errors.painPoints && <p className="text-sm text-danger-500 mt-1">{errors.painPoints}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              Describe your current operations in a few sentences <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-secondary-900/50 border-gray-300 dark:border-white/[0.1] text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-500/50 resize-none"
              placeholder="How do you handle work orders today? How do you coordinate vendors? What falls through the cracks?"
              value={operationsDescription}
              onChange={(e) => setOperationsDescription(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* ═══ Step 4: Goals, Outcomes & Timeline ═══ */}
      {currentStep === 4 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              What are your top goals? <span className="text-xs text-gray-400">(select all)</span>
            </label>
            <ChipSelect
              options={goals}
              selected={selectedGoals}
              onToggle={(v) => toggleMulti(selectedGoals, setSelectedGoals, v)}
            />
            {errors.goals && <p className="text-sm text-danger-500 mt-1">{errors.goals}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              What outcomes matter most to you? <span className="text-xs text-gray-400">(select all)</span>
            </label>
            <ChipSelect
              options={expectedOutcomes}
              selected={selectedOutcomes}
              onToggle={(v) => toggleMulti(selectedOutcomes, setSelectedOutcomes, v)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              What does success look like for you in 6 months? <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-secondary-900/50 border-gray-300 dark:border-white/[0.1] text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-500/50 resize-none"
              placeholder="e.g. 'All work orders tracked digitally, PM program in place, vendor costs down 15%, tenants stop complaining about response times...'"
              value={successDefinition}
              onChange={(e) => setSuccessDefinition(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              How soon do you need help?
            </label>
            <RadioSelect options={urgency} selected={selectedUrgency} onChange={setSelectedUrgency} />
            {errors.urgency && <p className="text-sm text-danger-500 mt-1">{errors.urgency}</p>}
          </div>

          <Input
            label="Monthly budget for operations tools / consulting (optional)"
            placeholder="e.g. $500-$2,000/mo"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
              Anything else we should know? <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-secondary-900/50 border-gray-300 dark:border-white/[0.1] text-gray-900 dark:text-secondary-100 placeholder:text-gray-400 dark:placeholder:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-500/50 resize-none"
              placeholder="Special requirements, certifications needed, timeline constraints, or anything else..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3 pt-2">
        {currentStep > 1 && (
          <Button type="button" variant="outline" size="lg" onClick={prevStep}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        )}
        {currentStep < STEPS.length ? (
          <Button type="button" variant="primary" size="lg" className="flex-1" onClick={nextStep}>
            Continue
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button type="button" variant="primary" size="lg" className="flex-1" onClick={handleSubmit}>
            Submit &amp; Get Started
          </Button>
        )}
      </div>

      {/* Login link — only on step 1 */}
      {currentStep === 1 && (
        <p className="text-center text-sm text-gray-500 dark:text-secondary-400">
          Already have an account?{' '}
          <NextLink href="/login" className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
            Sign in
          </NextLink>
        </p>
      )}
    </div>
  );
};

export { RegisterPage };
